<template>
  <div class="modal fade" id="coverageRulesModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-shield me-2"></i>
            Coverage Rules for {{ plan?.plan_name }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <!-- Tabs -->
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#current-rules" role="tab">
                <i class="bx bx-list-ul me-1"></i>Current Rules
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#add-rule" role="tab">
                <i class="bx bx-plus-circle me-1"></i>Add Rule
              </a>
            </li>
            <li class="nav-item d-none">
              <a class="nav-link" data-bs-toggle="tab" href="#import-services" role="tab">
                <i class="bx bx-import me-1"></i>Import Services
              </a>
            </li>
          </ul>

          <!-- Tab Content -->
          <div class="tab-content p-3">
            <!-- Current Rules Tab -->
            <div class="tab-pane active" id="current-rules">
              <div v-if="loadingRules" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <div v-else-if="coverageRules.length > 0" class="table-responsive">
                <table class="table table-hover">
                  <thead>
                  <tr>
                    <th>Service/Category</th>
                    <th>Coverage %</th>
                    <th>Copayment</th>
                    <th>Annual Limit</th>
                    <th>Per Visit Limit</th>
                    <th>Frequency</th>
                    <th>Pre-Auth</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="rule in coverageRules" :key="rule.rule_id">
                    <td>
                      <strong>{{ rule.service_name || rule.category_name }}</strong>
                      <br>
                      <small class="text-muted">{{ rule.service_id ? 'Service' : 'Category' }}</small>
                    </td>
                    <td>
                      <span class="badge bg-success">{{ rule.coverage_percentage }}%</span>
                    </td>
                    <td>{{ formatCurrency(rule.copayment_amount) }}</td>
                    <td>{{ formatCurrency(rule.annual_limit) }}</td>
                    <td>{{ formatCurrency(rule.per_visit_limit) }}</td>
                    <td>{{ rule.frequency_limit }}</td>
                    <td>
                      <span v-if="rule.requires_preauth" class="badge bg-warning">Required</span>
                      <span v-else class="badge bg-secondary">No</span>
                    </td>
                    <td>
                      <button
                          class="btn btn-sm btn-outline-danger"
                          @click="removeRule(rule)"
                          :disabled="removingRuleId === rule.rule_id"
                      >
                        <span v-if="removingRuleId === rule.rule_id" class="spinner-border spinner-border-sm me-1"></span>
                        <i v-else class="bx bx-trash"></i>
                        {{ removingRuleId === rule.rule_id ? 'Removing...' : '' }}
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div v-else class="text-center py-5">
                <i class="bx bx-shield display-1 text-muted"></i>
                <p class="text-muted">No coverage rules defined for this plan</p>
                <button class="btn btn-primary" @click="switchToAddTab">
                  <i class="bx bx-plus-circle me-1"></i>Add First Rule
                </button>
              </div>
            </div>

            <!-- Add Rule Tab -->
            <div class="tab-pane" id="add-rule">
              <!-- Validation Alert -->
              <div v-if="validationErrors.length > 0" class="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                <h6 class="alert-heading">Please fix the following errors:</h6>
                <ul class="mb-0">
                  <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
                </ul>
                <button type="button" class="btn-close" @click="validationErrors = []"></button>
              </div>

              <form @submit.prevent="addRule" novalidate>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Apply To <span class="text-danger">*</span></label>
                    <select
                        class="form-select"
                        :class="{'is-invalid': fieldErrors.applyTo}"
                        v-model="ruleForm.applyTo"
                        @change="clearSelection"
                        required
                    >
                      <option value="service">Specific Service</option>
                      <option value="category">Service Category</option>
                    </select>
                  </div>

                  <div class="col-md-6" v-if="ruleForm.applyTo === 'category'">
                    <label class="form-label">Category <span class="text-danger">*</span></label>
                    <select
                        class="form-select"
                        :class="{'is-invalid': fieldErrors.categoryId}"
                        v-model="ruleForm.categoryId"
                        required
                    >
                      <option value="">Select category</option>
                      <option v-for="category in categories" :key="category.category_id"
                              :value="category.category_id">
                        {{ category.category_name }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a category</div>
                  </div>

                  <div class="col-md-6" v-if="ruleForm.applyTo === 'service'">
                    <label class="form-label">Service <span class="text-danger">*</span></label>
                    <select
                        class="form-select"
                        :class="{'is-invalid': fieldErrors.serviceId}"
                        v-model="ruleForm.serviceId"
                        required
                    >
                      <option value="">Select service</option>
                      <optgroup v-for="category in servicesGrouped" :key="category.name"
                                :label="category.name">
                        <option v-for="service in category.services" :key="service.service_id"
                                :value="service.service_id">
                          {{ service.service_name }}
                        </option>
                      </optgroup>
                    </select>
                    <div class="invalid-feedback">Please select a service</div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label">Coverage Percentage <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <input
                          type="number"
                          class="form-control"
                          :class="{'is-invalid': fieldErrors.coveragePercentage}"
                          v-model.number="ruleForm.coveragePercentage"
                          min="0"
                          max="100"
                          step="1"
                          required
                      >
                      <span class="input-group-text">%</span>
                      <div class="invalid-feedback">Coverage must be between 0-100%</div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label">Copayment Amount <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          :class="{'is-invalid': fieldErrors.copaymentAmount}"
                          v-model.number="ruleForm.copaymentAmount"
                          min="0"
                          step="0.01"
                          required
                      >
                      <div class="invalid-feedback">Copayment must be 0 or greater</div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label">Deductible <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          :class="{'is-invalid': fieldErrors.deductibleAmount}"
                          v-model.number="ruleForm.deductibleAmount"
                          min="0"
                          step="0.01"
                          required
                      >
                      <div class="invalid-feedback">Deductible must be 0 or greater</div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label">Annual Limit <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          :class="{'is-invalid': fieldErrors.annualLimit}"
                          v-model.number="ruleForm.annualLimit"
                          min="0"
                          step="0.01"
                          required
                      >
                      <div class="invalid-feedback">Annual limit is required (minimum 0)</div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label">Per Visit Limit <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          :class="{'is-invalid': fieldErrors.perVisitLimit}"
                          v-model.number="ruleForm.perVisitLimit"
                          min="0"
                          step="0.01"
                          required
                      >
                      <div class="invalid-feedback">Per visit limit is required (minimum 0)</div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label">Frequency Limit <span class="text-danger">*</span></label>
                    <input
                        type="text"
                        class="form-control"
                        :class="{'is-invalid': fieldErrors.frequencyLimit}"
                        v-model="ruleForm.frequencyLimit"
                        placeholder="e.g., 2 per year, unlimited"
                        required
                    >
                    <div class="invalid-feedback">Please specify frequency limit</div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Waiting Period (Days) <span class="text-danger">*</span></label>
                    <input
                        type="number"
                        class="form-control"
                        :class="{'is-invalid': fieldErrors.waitingPeriodDays}"
                        v-model.number="ruleForm.waitingPeriodDays"
                        min="0"
                        required
                    >
                    <div class="invalid-feedback">Waiting period must be 0 or greater</div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Pre-Authorization Threshold <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          :class="{'is-invalid': fieldErrors.preauthThreshold}"
                          v-model.number="ruleForm.preauthThreshold"
                          min="0"
                          step="0.01"
                          required
                      >
                      <div class="invalid-feedback">Pre-auth threshold is required (minimum 0)</div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" v-model="ruleForm.requiresPreauth">
                      <label class="form-check-label">Requires Pre-Authorization</label>
                    </div>
                  </div>

                  <div class="col-12">
                    <label class="form-label">Special Conditions <span class="text-danger">*</span></label>
                    <textarea
                        class="form-control"
                        :class="{'is-invalid': fieldErrors.specialConditions}"
                        rows="2"
                        v-model="ruleForm.specialConditions"
                        placeholder="Any special conditions or notes (enter 'None' if not applicable)"
                        required
                    ></textarea>
                    <div class="invalid-feedback">Please specify special conditions or enter 'None'</div>
                  </div>

                  <div class="col-12">
                    <button type="submit" class="btn btn-primary" :disabled="savingRule">
                      <span v-if="savingRule" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="bx bx-plus-circle me-1"></i>
                      {{ savingRule ? 'Adding Rule...' : 'Add Coverage Rule' }}
                    </button>
                    <button type="button" class="btn btn-outline-secondary ms-2" @click="resetForm">
                      <i class="bx bx-refresh me-1"></i>Reset Form
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <!-- Import Services Tab -->
            <div class="tab-pane" id="import-services">
              <div class="mb-3">
                <h6>Import Services from Template</h6>
                <p class="text-muted">Upload a CSV file with services and their coverage rules</p>
              </div>

              <div class="mb-3">
                <label class="form-label">CSV File</label>
                <input type="file" class="form-control" @change="handleFileUpload" accept=".csv">
              </div>

              <div v-if="importPreview.length > 0" class="mb-3">
                <h6>Preview (First 5 rows)</h6>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Service Name</th>
                      <th>Category</th>
                      <th>Coverage %</th>
                      <th>Annual Limit</th>
                      <th>Frequency</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(row, index) in importPreview.slice(0, 5)" :key="index">
                      <td>{{ row.service_name }}</td>
                      <td>{{ row.category }}</td>
                      <td>{{ row.coverage_percentage }}%</td>
                      <td>{{ row.annual_limit }}</td>
                      <td>{{ row.frequency_limit }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <button class="btn btn-success" @click="importServices" :disabled="importingServices">
                  <span v-if="importingServices" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bx bx-import me-1"></i>
                  {{ importingServices ? 'Importing...' : `Import ${importPreview.length} Services` }}
                </button>
              </div>

              <div class="alert alert-info">
                <h6>CSV Format:</h6>
                <p class="mb-0">service_code, service_name, category, service_type, coverage_percentage, copayment_amount, deductible_amount, annual_limit, per_visit_limit, frequency_limit, waiting_period_days, requires_preauth, preauth_threshold, special_conditions</p>
              </div>

              <div class="alert alert-warning">
                <h6>Important:</h6>
                <ul class="mb-0">
                  <li>All fields are mandatory in the CSV file</li>
                  <li>Coverage percentage must be between 0-100</li>
                  <li>All amount fields must be numeric values</li>
                  <li>Waiting period must be in days (0 or greater)</li>
                  <li>requires_preauth should be 'true' or 'false'</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "CoverageRulesModal",
  props: {
    plan: {
      type: Object,
      default: null
    },
    services: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loadingRules: false,
      coverageRules: [],
      removingRuleId: null,
      savingRule: false,
      importingServices: false,
      validationErrors: [],
      fieldErrors: {},
      ruleForm: {
        applyTo: 'service',
        serviceId: '',
        categoryId: '',
        coveragePercentage: 100,
        copaymentAmount: 0,
        deductibleAmount: 0,
        annualLimit: 0,
        perVisitLimit: 0,
        frequencyLimit: '',
        waitingPeriodDays: 0,
        requiresPreauth: false,
        preauthThreshold: 0,
        specialConditions: ''
      },
      importPreview: [],
      importFile: null
    };
  },
  computed: {
    servicesGrouped() {
      const grouped = {};
      this.services.forEach(service => {
        const category = service.category_name || 'Uncategorized';
        if (!grouped[category]) {
          grouped[category] = {
            name: category,
            services: []
          };
        }
        grouped[category].services.push(service);
      });
      return Object.values(grouped);
    }
  },
  methods: {
    openModal() {
      this.loadCoverageRules();
      this.loadServices();
      const modal = new bootstrap.Modal(document.getElementById('coverageRulesModal'));
      modal.show();
    },

    async loadCoverageRules() {
      if (!this.plan) return;

      this.loadingRules = true;
      const data = {
        "function": "getPlanCoverageDetails",
        "planId": this.plan.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.coverageRules = res.data.data.coverage_rules || [];
        }
      } catch (e) {
        console.error("Failed to load coverage rules:", e);
        this.$emit('show-alert', {
          type: 'danger',
          message: 'Failed to load coverage rules'
        });
      } finally {
        this.loadingRules = false;
      }
    },

    async loadServices() {
      const data = {
        "function": "getServicesByCategory",
        "planId": this.plan?.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.$emit('services-loaded', res.data.data.services);
        }
      } catch (e) {
        console.error("Failed to load services:", e);
      }
    },

    validateForm() {
      this.validationErrors = [];
      this.fieldErrors = {};
      let isValid = true;

      // Validate service/category selection
      if (this.ruleForm.applyTo === 'service') {
        if (!this.ruleForm.serviceId) {
          this.validationErrors.push('Please select a service');
          this.fieldErrors.serviceId = true;
          isValid = false;
        }
      } else {
        if (!this.ruleForm.categoryId) {
          this.validationErrors.push('Please select a category');
          this.fieldErrors.categoryId = true;
          isValid = false;
        }
      }

      // Validate coverage percentage
      if (this.ruleForm.coveragePercentage === null ||
          this.ruleForm.coveragePercentage === '' ||
          this.ruleForm.coveragePercentage < 0 ||
          this.ruleForm.coveragePercentage > 100) {
        this.validationErrors.push('Coverage percentage must be between 0 and 100');
        this.fieldErrors.coveragePercentage = true;
        isValid = false;
      }

      // Validate copayment amount
      if (this.ruleForm.copaymentAmount === null ||
          this.ruleForm.copaymentAmount === '' ||
          this.ruleForm.copaymentAmount < 0) {
        this.validationErrors.push('Copayment amount must be 0 or greater');
        this.fieldErrors.copaymentAmount = true;
        isValid = false;
      }

      // Validate deductible amount
      if (this.ruleForm.deductibleAmount === null ||
          this.ruleForm.deductibleAmount === '' ||
          this.ruleForm.deductibleAmount < 0) {
        this.validationErrors.push('Deductible amount must be 0 or greater');
        this.fieldErrors.deductibleAmount = true;
        isValid = false;
      }

      // Validate annual limit
      if (this.ruleForm.annualLimit === null ||
          this.ruleForm.annualLimit === '' ||
          this.ruleForm.annualLimit < 0) {
        this.validationErrors.push('Annual limit is required (minimum 0)');
        this.fieldErrors.annualLimit = true;
        isValid = false;
      }

      // Validate per visit limit
      if (this.ruleForm.perVisitLimit === null ||
          this.ruleForm.perVisitLimit === '' ||
          this.ruleForm.perVisitLimit < 0) {
        this.validationErrors.push('Per visit limit is required (minimum 0)');
        this.fieldErrors.perVisitLimit = true;
        isValid = false;
      }

      // Validate frequency limit
      if (!this.ruleForm.frequencyLimit || this.ruleForm.frequencyLimit.trim() === '') {
        this.validationErrors.push('Frequency limit is required');
        this.fieldErrors.frequencyLimit = true;
        isValid = false;
      }

      // Validate waiting period
      if (this.ruleForm.waitingPeriodDays === null ||
          this.ruleForm.waitingPeriodDays === '' ||
          this.ruleForm.waitingPeriodDays < 0) {
        this.validationErrors.push('Waiting period must be 0 or greater');
        this.fieldErrors.waitingPeriodDays = true;
        isValid = false;
      }

      // Validate pre-auth threshold
      if (this.ruleForm.preauthThreshold === null ||
          this.ruleForm.preauthThreshold === '' ||
          this.ruleForm.preauthThreshold < 0) {
        this.validationErrors.push('Pre-authorization threshold is required (minimum 0)');
        this.fieldErrors.preauthThreshold = true;
        isValid = false;
      }

      // Validate special conditions
      if (!this.ruleForm.specialConditions || this.ruleForm.specialConditions.trim() === '') {
        this.validationErrors.push('Special conditions are required (enter "None" if not applicable)');
        this.fieldErrors.specialConditions = true;
        isValid = false;
      }

      return isValid;
    },

    async addRule() {
      // Validate form first
      if (!this.validateForm()) {
        // Scroll to top to show validation errors
        const modalBody = document.querySelector('#coverageRulesModal .modal-body');
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
        return;
      }

      this.savingRule = true;

      const data = {
        "function": "addServiceCoverageRule",
        "planId": this.plan.plan_id,
        ...(this.ruleForm.applyTo === 'service'
            ? { serviceId: this.ruleForm.serviceId }
            : { categoryId: this.ruleForm.categoryId }),
        "coveragePercentage": this.ruleForm.coveragePercentage,
        "copaymentAmount": this.ruleForm.copaymentAmount,
        "deductibleAmount": this.ruleForm.deductibleAmount,
        "annualLimit": this.ruleForm.annualLimit,
        "perVisitLimit": this.ruleForm.perVisitLimit,
        "frequencyLimit": this.ruleForm.frequencyLimit,
        "waitingPeriodDays": this.ruleForm.waitingPeriodDays,
        "requiresPreauth": this.ruleForm.requiresPreauth,
        "preauthThreshold": this.ruleForm.preauthThreshold,
        "specialConditions": this.ruleForm.specialConditions
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.$emit('rules-updated', {
            success: true,
            message: 'Coverage rule added successfully'
          });
          this.resetForm();
          this.loadCoverageRules();
          // Switch to current rules tab
          const tab = new bootstrap.Tab(document.querySelector('a[href="#current-rules"]'));
          tab.show();
        } else {
          this.validationErrors = [res.data.message || 'Failed to add coverage rule'];
        }
      } catch (e) {
        console.error('Error adding coverage rule:', e);
        this.validationErrors = [e.message || 'Failed to add coverage rule'];
      } finally {
        this.savingRule = false;
      }
    },

    async removeRule(rule) {
      if (!confirm(`Are you sure you want to remove this coverage rule for "${rule.service_name || rule.category_name}"?`)) {
        return;
      }

      this.removingRuleId = rule.rule_id;

      const data = {
        "function": "removeCoverageRule",
        "ruleId": rule.rule_id,
        "planId": this.plan.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.$emit('rules-updated', {
            success: true,
            message: 'Coverage rule removed successfully'
          });
          // Remove the rule from local array immediately for better UX
          this.coverageRules = this.coverageRules.filter(r => r.rule_id !== rule.rule_id);
        } else {
          this.$emit('show-alert', {
            type: 'danger',
            message: res.data.message || 'Failed to remove coverage rule'
          });
        }
      } catch (e) {
        console.error('Error removing coverage rule:', e);
        this.$emit('show-alert', {
          type: 'danger',
          message: 'Failed to remove coverage rule. Please try again.'
        });
      } finally {
        this.removingRuleId = null;
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const csv = e.target.result;
        this.parseCSV(csv);
      };
      reader.readAsText(file);
    },

    parseCSV(csv) {
      const lines = csv.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      // Validate required headers
      const requiredHeaders = [
        'service_code', 'service_name', 'category', 'service_type',
        'coverage_percentage', 'copayment_amount', 'deductible_amount',
        'annual_limit', 'per_visit_limit', 'frequency_limit',
        'waiting_period_days', 'requires_preauth', 'preauth_threshold',
        'special_conditions'
      ];

      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) {
        alert(`Missing required columns in CSV: ${missingHeaders.join(', ')}`);
        return;
      }

      this.importPreview = [];
      let validationErrors = [];

      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim());
          const row = {};

          headers.forEach((header, index) => {
            row[header] = values[index];
          });

          // Validate row data
          if (!row.service_name) {
            validationErrors.push(`Row ${i}: Service name is required`);
          }
          if (!row.category) {
            validationErrors.push(`Row ${i}: Category is required`);
          }
          if (isNaN(row.coverage_percentage) || row.coverage_percentage < 0 || row.coverage_percentage > 100) {
            validationErrors.push(`Row ${i}: Invalid coverage percentage`);
          }
          if (isNaN(row.copayment_amount) || row.copayment_amount < 0) {
            validationErrors.push(`Row ${i}: Invalid copayment amount`);
          }
          if (isNaN(row.annual_limit) || row.annual_limit < 0) {
            validationErrors.push(`Row ${i}: Invalid annual limit`);
          }
          if (!row.frequency_limit) {
            validationErrors.push(`Row ${i}: Frequency limit is required`);
          }

          this.importPreview.push(row);
        }
      }

      if (validationErrors.length > 0) {
        alert(`CSV validation errors:\n${validationErrors.slice(0, 5).join('\n')}${validationErrors.length > 5 ? '\n...' : ''}`);
        this.importPreview = [];
      }
    },

    async importServices() {
      if (this.importPreview.length === 0) {
        alert('No data to import');
        return;
      }

      this.importingServices = true;

      const data = {
        "function": "importServicesAndCoverage",
        "planId": this.plan.plan_id,
        "services": this.importPreview
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.$emit('rules-updated', {
            success: true,
            message: `Imported ${res.data.data.imported} services successfully`
          });
          this.importPreview = [];
          this.loadCoverageRules();
          // Switch to current rules tab
          const tab = new bootstrap.Tab(document.querySelector('a[href="#current-rules"]'));
          tab.show();
        } else {
          alert(res.data.message || 'Failed to import services');
        }
      } catch (e) {
        alert(e.message || 'Failed to import services');
      } finally {
        this.importingServices = false;
      }
    },

    switchToAddTab() {
      const tab = new bootstrap.Tab(document.querySelector('a[href="#add-rule"]'));
      tab.show();
    },

    clearSelection() {
      this.ruleForm.serviceId = '';
      this.ruleForm.categoryId = '';
      this.fieldErrors.serviceId = false;
      this.fieldErrors.categoryId = false;
    },

    resetForm() {
      this.ruleForm = {
        applyTo: 'service',
        serviceId: '',
        categoryId: '',
        coveragePercentage: 100,
        copaymentAmount: 0,
        deductibleAmount: 0,
        annualLimit: 0,
        perVisitLimit: 0,
        frequencyLimit: '',
        waitingPeriodDays: 0,
        requiresPreauth: false,
        preauthThreshold: 0,
        specialConditions: ''
      };
      this.validationErrors = [];
      this.fieldErrors = {};
    },

    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return 'Not Set';
      }
      return `ETB ${new Intl.NumberFormat().format(amount)}`;
    }
  }
};
</script>

<style scoped>
.is-invalid ~ .invalid-feedback {
  display: block;
}

.input-group > .form-control.is-invalid ~ .invalid-feedback {
  display: block;
}
</style>